import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box, Typography, Button, Card, CardContent, Avatar, Chip, Divider,
  Stack, CircularProgress, alpha, Paper, Drawer, IconButton,
  useTheme, useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon, Check as CheckIcon,
  ArrowBack, ArrowForward, PersonOutline, EmojiEvents,
  Description, AccessTime, Inbox,
  HourglassEmpty, CheckCircleOutline, CancelOutlined,
} from "@mui/icons-material";
import Footer from "../globalComponents/footer";
import TopBar from "../globalComponents/topbar";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorDark: "#B91C1C",
  errorSoft: "#FEE2E2",
};

// Status → visual config (same source of truth as PendingRequests page)
const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    icon: <HourglassEmpty sx={{ fontSize: 12 }} />,
    bg: C.warningSoft, fg: C.warning,
    rank: 0,
  },
  accepted: {
    label: "Accepted",
    icon: <CheckCircleOutline sx={{ fontSize: 12 }} />,
    bg: C.secondarySoft, fg: C.secondary,
    rank: 1,
  },
  rejected: {
    label: "Declined",
    icon: <CancelOutlined sx={{ fontSize: 12 }} />,
    bg: C.errorSoft, fg: C.error,
    rank: 2,
  },
};

const statusOf = (req) => (req.status || "pending").toLowerCase();
const statusConfig = (req) => STATUS_CONFIG[statusOf(req)] || STATUS_CONFIG.pending;

export default function MentorRequests() {
  const [requests, setRequests] = useState([]);
  const [learners, setLearners] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const itemsPerPage = 4;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // ── Fetch requests ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem("user"));
        const mentorId = user?.id || user?._id;
        if (!mentorId) { setError("Mentor not found in local storage."); return; }
        const res = await axios.get(
          `https://backend-global-1vq9.onrender.com/api/connection-requests/mentor/${mentorId}`
        );
        setRequests(res.data?.data || []);
      } catch { setError("Failed to fetch connection requests."); }
      finally { setLoading(false); }
    })();
  }, []);

  // ── Accept ─────────────────────────────────────────────────────────────
  const handleAccept = async (requestId) => {
    try {
      await axios.put(
        `https://backend-global-1vq9.onrender.com/api/connection-requests/${requestId}/accept`
      );
      setRequests((prev) => prev.map((r) => r._id === requestId ? { ...r, status: "accepted" } : r));
      if (selectedRequest?._id === requestId)
        setSelectedRequest((prev) => ({ ...prev, status: "accepted" }));
    } catch { alert("Failed to accept request"); }
  };

  // ── Reject ─────────────────────────────────────────────────────────────
  const handleReject = async (requestId) => {
    try {
      await axios.put(
        `https://backend-global-1vq9.onrender.com/api/connection-requests/${requestId}/reject`
      );
      setRequests((prev) => prev.map((r) => r._id === requestId ? { ...r, status: "rejected" } : r));
      if (selectedRequest?._id === requestId)
        setSelectedRequest((prev) => ({ ...prev, status: "rejected" }));
    } catch { alert("Failed to reject request"); }
  };

  // ── Fetch learners ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("https://backend-global-1vq9.onrender.com/api/learner/list");
        setLearners(res.data?.learners || []);
      } catch { console.error("Error fetching learners"); }
    })();
  }, []);

  // ── Enrich + sort ──────────────────────────────────────────────────────
  const enrichedRequests = requests
    .map((req) => {
      const mentee = learners.find((l) => l.userId === req.menteeId);
      return { ...req, menteeName: mentee?.fullName || "Unknown", menteeEmail: mentee?.email || "No email" };
    })
    .filter((r) => r.menteeName !== "Unknown")
    .sort((a, b) => {
      const ra = statusConfig(a).rank;
      const rb = statusConfig(b).rank;
      if (ra !== rb) return ra - rb;
      const da = new Date(a.createdAt || 0).getTime();
      const db = new Date(b.createdAt || 0).getTime();
      return db - da;
    });

  // ── Pagination ─────────────────────────────────────────────────────────
  const totalPages = Math.ceil(enrichedRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRequests = enrichedRequests.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    if (enrichedRequests.length > 0 && !selectedRequest)
      setSelectedRequest(enrichedRequests[0]);
  }, [learners, requests]);

  // ── Status counts for hero chips ──────────────────────────────────────
  const counts = {
    pending: enrichedRequests.filter((r) => statusOf(r) === "pending").length,
    accepted: enrichedRequests.filter((r) => statusOf(r) === "accepted").length,
    rejected: enrichedRequests.filter((r) => statusOf(r) === "rejected").length,
  };

  // ── Detail panel — shared between side card (desktop) and drawer (mobile) ─
  const DetailPanel = () => {
    if (!selectedRequest) return (
      <Box height="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <Box sx={{
          width: 80, height: 80, borderRadius: "50%",
          background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
          display: "flex", alignItems: "center", justifyContent: "center", mb: 3,
          boxShadow: `0 8px 20px ${C.primary}30`,
        }}>
          <PersonOutline sx={{ fontSize: 36, color: "#fff" }} />
        </Box>
        <Typography variant="h6" sx={{ color: C.textSecondary, fontWeight: 500, textAlign: "center" }}>
          Select a request to view details
        </Typography>
      </Box>
    );

    const sc = statusConfig(selectedRequest);

    return (
      <>
        {/* Panel header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 6, height: 24,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
              borderRadius: 1, flexShrink: 0,
            }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: C.textPrimary, fontSize: { xs: 15, sm: 17 } }}>
              Request Details
            </Typography>
          </Stack>
          {isMobile && (
            <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: C.textSecondary }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Mentee gradient banner */}
        <Box sx={{
          position: "relative",
          p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3,
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
          boxShadow: `0 8px 24px ${C.primary}30`,
          overflow: "hidden",
        }}>
          {/* Green orb decoration */}
          <Box sx={{
            position: "absolute",
            top: "-50%", right: "-15%",
            width: 160, height: 160,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Stack direction="row" spacing={2} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Avatar sx={{
              width: { xs: 52, sm: 72 }, height: { xs: 52, sm: 72 },
              bgcolor: "#fff", color: C.primary,
              fontSize: { xs: 20, sm: 28 }, fontWeight: 700,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)", flexShrink: 0,
            }}>
              {selectedRequest.menteeName.charAt(0).toUpperCase()}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography sx={{
                fontWeight: 700, fontSize: { xs: 15, sm: 18 },
                color: "white", mb: 0.5,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {selectedRequest.menteeName}
              </Typography>
              <Typography variant="body2" sx={{
                color: "rgba(255,255,255,0.9)", fontWeight: 500,
                fontSize: { xs: 11, sm: 13 },
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {selectedRequest.menteeEmail}
              </Typography>
              <Chip
                label={sc.label}
                size="small"
                sx={{
                  mt: 0.75,
                  bgcolor: "rgba(255,255,255,0.22)", color: "#fff",
                  fontWeight: 700, height: 22,
                  fontSize: { xs: 10, sm: 12 },
                }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Detail fields */}
        <Stack spacing={2.5}>
          {[
            { icon: <EmojiEvents sx={{ fontSize: 15 }} />, label: "Goal", value: selectedRequest.goal },
            { icon: <Description sx={{ fontSize: 15 }} />, label: "Description", value: selectedRequest.description || "No description provided" },
            { icon: <AccessTime sx={{ fontSize: 15 }} />, label: "Request Date",
              value: new Date(selectedRequest.createdAt).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) },
          ].map((field, i) => (
            <React.Fragment key={i}>
              <Box>
                <Typography variant="overline" sx={{
                  color: C.textMuted, fontWeight: 700, letterSpacing: 1,
                  fontSize: "0.7rem", display: "flex", alignItems: "center", gap: 0.5,
                }}>
                  {field.icon}{field.label}
                </Typography>
                <Typography variant="body2" sx={{
                  color: C.textPrimary, mt: 0.5, fontWeight: 500,
                  lineHeight: 1.7, fontSize: { xs: 12, sm: 14 },
                }}>
                  {field.value}
                </Typography>
              </Box>
              {i < 2 && <Divider sx={{ borderColor: C.borderSoft }} />}
            </React.Fragment>
          ))}

          {/* Status pill */}
          <Divider sx={{ borderColor: C.borderSoft }} />
          <Box>
            <Typography variant="overline" sx={{
              color: C.textMuted, fontWeight: 700, letterSpacing: 1, fontSize: "0.7rem",
            }}>
              Status
            </Typography>
            <Box mt={1}>
              <Chip
                icon={sc.icon}
                label={sc.label}
                sx={{
                  bgcolor: sc.bg, color: sc.fg,
                  fontWeight: 700, fontSize: "0.8rem",
                  py: 2, px: 1,
                  "& .MuiChip-icon": { color: sc.fg },
                }}
              />
            </Box>
          </Box>
        </Stack>

        {/* Action buttons — only when pending */}
        {statusOf(selectedRequest) === "pending" && (
          <>
            <Divider sx={{ my: 3, borderColor: C.borderSoft }} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained" startIcon={<CloseIcon />} fullWidth
                onClick={() => handleReject(selectedRequest._id)}
                sx={{
                  py: { xs: 1, sm: 1.5 }, textTransform: "none", fontWeight: 700,
                  borderRadius: 2.5,
                  bgcolor: C.error, color: "#fff",
                  boxShadow: `0 4px 14px ${C.error}30`,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  "&:hover": {
                    bgcolor: C.errorDark,
                    boxShadow: `0 6px 18px ${C.error}45`,
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Decline Request
              </Button>
              <Button
                variant="contained" startIcon={<CheckIcon />} fullWidth
                onClick={() => handleAccept(selectedRequest._id)}
                sx={{
                  py: { xs: 1, sm: 1.5 }, textTransform: "none", fontWeight: 700,
                  borderRadius: 2.5,
                  background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryDark} 100%)`,
                  boxShadow: `0 4px 14px ${C.secondary}30`,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  "&:hover": {
                    boxShadow: `0 6px 18px ${C.secondary}45`,
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Accept Request
              </Button>
            </Stack>
          </>
        )}
      </>
    );
  };

  // ── Loading state ──────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ bgcolor: C.bg, minHeight: "100vh" }}>
      <TopBar />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh" }}>
        <CircularProgress size={48} sx={{ color: C.primary }} />
      </Box>
    </Box>
  );

  // ── Error state ────────────────────────────────────────────────────────
  if (error) return (
    <Box sx={{ bgcolor: C.bg, minHeight: "100vh" }}>
      <TopBar />
      <Box sx={{ px: { xs: "12px", sm: "24px" }, py: 4, textAlign: "center" }}>
        <Typography sx={{
          color: C.error, fontWeight: 600,
          bgcolor: C.errorSoft, p: 3, borderRadius: 3,
          border: `1px solid ${alpha(C.error, 0.2)}`,
        }}>
          {error}
        </Typography>
      </Box>
    </Box>
  );

  // ── Main UI ────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      display: "flex", flexDirection: "column", bgcolor: C.bg, minHeight: "100vh",
      overflowX: "hidden", width: "100%", boxSizing: "border-box",
    }}>
      <TopBar />

      <Box sx={{
        flex: 1, px: { xs: "12px", sm: "20px", md: "32px" }, py: { xs: 3, sm: 4 },
        maxWidth: "1400px", mx: "auto", width: "100%", boxSizing: "border-box",
      }}>

        {/* ── Hero header ────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3.5 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 180, sm: 240 }, height: { xs: 180, sm: 240 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography sx={{
              fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
              textTransform: "uppercase", color: "rgba(255,255,255,0.7)", mb: 0.75,
            }}>
              Mentor Console
            </Typography>
            <Typography sx={{
              fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
              color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
            }}>
              Connection Requests
            </Typography>
            <Typography sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: 13, sm: 14 },
              maxWidth: 520, lineHeight: 1.5,
            }}>
              Review and respond to incoming mentorship requests from learners
            </Typography>

            {enrichedRequests.length > 0 && (
              <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                {counts.pending > 0 && (
                  <Chip
                    icon={<HourglassEmpty sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                    label={`${counts.pending} pending`}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "#fff", fontWeight: 700,
                      backdropFilter: "blur(4px)",
                      "& .MuiChip-icon": { color: "#fff" },
                    }}
                  />
                )}
                {counts.accepted > 0 && (
                  <Chip
                    icon={<CheckCircleOutline sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                    label={`${counts.accepted} accepted`}
                    size="small"
                    sx={{
                      bgcolor: alpha(C.secondary, 0.85),
                      color: "#fff", fontWeight: 700,
                      "& .MuiChip-icon": { color: "#fff" },
                    }}
                  />
                )}
                {counts.rejected > 0 && (
                  <Chip
                    icon={<CancelOutlined sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                    label={`${counts.rejected} declined`}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "#fff", fontWeight: 700,
                      "& .MuiChip-icon": { color: "#fff" },
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>
        </Paper>

        {/* ── Main layout ───────────────────────────────────────────── */}
        <Box sx={{
          display: "flex", flexDirection: { xs: "column", lg: "row" },
          gap: { xs: 2, sm: 3 }, width: "100%", boxSizing: "border-box",
        }}>

          {/* ── LEFT: Request list ────────────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              width: { xs: "100%", lg: "420px" }, flexShrink: 0,
              borderRadius: 3,
              border: `1px solid ${C.border}`,
              bgcolor: C.paper,
              p: { xs: 2, sm: 3 },
              display: "flex", flexDirection: "column",
              maxHeight: { lg: "85vh" }, boxSizing: "border-box",
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "3px",
                background: `linear-gradient(90deg, ${C.primary}, ${C.primary}88)`,
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5} mt={0.5}>
              <Typography variant="h6" fontWeight={700} sx={{
                color: C.textPrimary, flex: 1, fontSize: { xs: 15, sm: 16 },
                letterSpacing: -0.2,
              }}>
                All Requests
              </Typography>
              <Chip
                label={enrichedRequests.length}
                size="small"
                sx={{
                  bgcolor: C.primarySoft, color: C.primary,
                  fontWeight: 700, height: 22,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Stack>

            {/* Request cards */}
            <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 2 }}>
              <Stack spacing={1.25}>
                {paginatedRequests.length === 0 ? (
                  <Box sx={{
                    textAlign: "center", py: 6, px: 2,
                    bgcolor: C.primarySofter, borderRadius: 3,
                  }}>
                    <Box sx={{
                      width: 64, height: 64, borderRadius: "50%",
                      background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 2,
                      boxShadow: `0 6px 16px ${C.primary}30`,
                    }}>
                      <Inbox sx={{ fontSize: 28, color: "#fff" }} />
                    </Box>
                    <Typography sx={{
                      color: C.textPrimary, fontWeight: 700, mb: 1,
                      fontSize: { xs: 14, sm: 16 },
                    }}>
                      No Requests Yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: C.textSecondary, fontSize: { xs: 12, sm: 14 } }}>
                      You don't have any connection requests at the moment.
                    </Typography>
                  </Box>
                ) : (
                  paginatedRequests.map((req) => {
                    const isSelected = selectedRequest?._id === req._id;
                    const initial = req.menteeName?.charAt(0).toUpperCase() || "?";
                    const sc = statusConfig(req);

                    return (
                      <Card
                        key={req._id}
                        elevation={0}
                        onClick={() => {
                          setSelectedRequest(req);
                          if (isMobile) setDrawerOpen(true);
                        }}
                        sx={{
                          border: `1.5px solid ${isSelected ? C.primary : C.borderSoft}`,
                          borderRadius: 2.5, cursor: "pointer",
                          transition: "all 0.2s ease",
                          bgcolor: isSelected ? C.primarySofter : C.paper,
                          boxSizing: "border-box",
                          "&:hover": {
                            borderColor: C.primary,
                            bgcolor: C.primarySofter,
                            transform: "translateX(2px)",
                            boxShadow: "0 4px 12px rgba(10,46,138,0.08)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5, gap: 1 }}>
                            <Avatar sx={{
                              width: { xs: 38, sm: 48 }, height: { xs: 38, sm: 48 },
                              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                              color: "#fff",
                              fontWeight: 700, fontSize: { xs: "1rem", sm: "1.15rem" },
                              boxShadow: `0 4px 10px ${C.primary}25`, flexShrink: 0,
                            }}>
                              {initial}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{
                                fontWeight: 700, color: C.textPrimary, mb: 0.3,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                fontSize: { xs: 12, sm: 14 },
                              }}>
                                {req.menteeName}
                              </Typography>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <AccessTime sx={{ fontSize: 11, color: C.textMuted }} />
                                <Typography sx={{
                                  color: C.textMuted, fontSize: { xs: "0.65rem", sm: "0.75rem" }, fontWeight: 600,
                                }}>
                                  {new Date(req.createdAt).toLocaleDateString()}
                                </Typography>
                              </Stack>
                            </Box>
                            <Chip
                              icon={sc.icon}
                              label={sc.label}
                              size="small"
                              sx={{
                                height: 22, fontSize: "0.65rem", fontWeight: 700,
                                bgcolor: sc.bg, color: sc.fg, flexShrink: 0,
                                "& .MuiChip-icon": { color: sc.fg },
                                "& .MuiChip-label": { px: 0.5 },
                              }}
                            />
                          </Box>

                          <Typography sx={{
                            color: C.textSecondary, display: "block", mb: 1.5,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            fontSize: { xs: "0.7rem", sm: "0.78rem" },
                          }}>
                            <Box component="span" sx={{ fontWeight: 700, color: C.textPrimary }}>Goal:</Box>{" "}
                            {req.goal}
                          </Typography>

                          {statusOf(req) === "pending" && (
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="contained" size="small"
                                startIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                                onClick={(e) => { e.stopPropagation(); handleReject(req._id); }}
                                sx={{
                                  flex: 1, bgcolor: C.error, color: "#fff",
                                  fontWeight: 700, fontSize: { xs: "0.7rem", sm: "0.78rem" },
                                  textTransform: "none", borderRadius: 2, py: 0.5,
                                  boxShadow: "none",
                                  "&:hover": { bgcolor: C.errorDark, boxShadow: `0 2px 8px ${C.error}30` },
                                }}
                              >
                                Decline
                              </Button>
                              <Button
                                variant="contained" size="small"
                                startIcon={<CheckIcon sx={{ fontSize: 12 }} />}
                                onClick={(e) => { e.stopPropagation(); handleAccept(req._id); }}
                                sx={{
                                  flex: 1, bgcolor: C.secondary, color: "#fff",
                                  fontWeight: 700, fontSize: { xs: "0.7rem", sm: "0.78rem" },
                                  textTransform: "none", borderRadius: 2, py: 0.5,
                                  boxShadow: "none",
                                  "&:hover": { bgcolor: C.secondaryDark, boxShadow: `0 2px 8px ${C.secondary}30` },
                                }}
                              >
                                Accept
                              </Button>
                            </Stack>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            </Box>

            {/* Pagination */}
            {enrichedRequests.length > itemsPerPage && (
              <Box sx={{ pt: 2, borderTop: `1px solid ${C.borderSoft}` }}>
                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                  <Button
                    startIcon={<ArrowBack />} disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    sx={{
                      textTransform: "none", fontWeight: 700,
                      fontSize: { xs: 11, sm: 13 },
                      color: currentPage === 1 ? C.textMuted : C.primary,
                      "&:hover": { bgcolor: currentPage === 1 ? "transparent" : C.primarySoft },
                    }}
                  >
                    Prev
                  </Button>
                  <Typography sx={{
                    fontWeight: 700, color: C.textSecondary, fontSize: { xs: 11, sm: 13 },
                  }}>
                    {currentPage} / {totalPages}
                  </Typography>
                  <Button
                    endIcon={<ArrowForward />} disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    sx={{
                      textTransform: "none", fontWeight: 700,
                      fontSize: { xs: 11, sm: 13 },
                      color: currentPage === totalPages ? C.textMuted : C.primary,
                      "&:hover": { bgcolor: currentPage === totalPages ? "transparent" : C.primarySoft },
                    }}
                  >
                    Next
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>

          {/* ── RIGHT: Detail panel — desktop only (lg+) ─────────── */}
          {!isMobile && (
            <Card
              elevation={0}
              sx={{
                position: "relative",
                flex: 1, minWidth: 0,
                borderRadius: 3,
                border: `1px solid ${C.border}`,
                bgcolor: C.paper,
                p: { sm: 3, md: 4 },
                minHeight: { lg: "85vh" },
                overflowY: "auto",
                boxSizing: "border-box",
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0, left: 0, right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`,
                },
              }}
            >
              <DetailPanel />
            </Card>
          )}
        </Box>
      </Box>

      {/* ── Mobile/Tablet: detail in bottom drawer ────────────────────── */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: "90vh",
              overflowY: "auto",
              px: { xs: "16px", sm: "24px" },
              pt: "16px",
              pb: "32px",
            },
          }}
        >
          <Box sx={{ width: 40, height: 4, bgcolor: C.borderSoft, borderRadius: 2, mx: "auto", mb: 2.5 }} />
          <DetailPanel />
        </Drawer>
      )}

      <Footer />
    </Box>
  );
}
